// ═══════════════════════════════════════════════════════════════
// DEBRIS ADMS — GIS Map
// Live map with zones, disposal sites, recent pickups, heat overlay
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Group, Paper, Badge, Stack, Text, Switch, Select, Loader, Center, SegmentedControl, Button,
} from '../../components/gab/GabComponents';
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import { useDebrisStore } from '../../store/debrisStore';
import { useIncidentZones } from '../../hooks/useIncidentZones';
import ZoneMapOverlay from '../../components/map/ZoneMapOverlay';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useAuthStore } from '../../store/authStore';
import { DEBRIS_TYPE_LABELS } from '../../types/debris';
import type { DebrisType } from '../../types/debris';

// Fix leaflet default icon
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
const DefaultIcon = L.icon({ iconUrl, shadowUrl: iconShadow, iconAnchor: [12, 41], popupAnchor: [1, -34] });
L.Marker.prototype.options.icon = DefaultIcon;

// Custom icons
function makeIcon(color: string) {
  return L.divIcon({
    className: '',
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.4)"></div>`,
  });
}

const ZONE_ICON = makeIcon('#16a34a');
const SITE_ICON = makeIcon('#dc2626');
const PICKUP_ICON = makeIcon('#d97706');
const STOP_ICON = L.divIcon({
  className: '',
  iconSize: [10, 10],
  iconAnchor: [5, 5],
  html: `<div style="width:10px;height:10px;border-radius:50%;background:#f59e0b;border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.3)"></div>`,
});

// Leaflet.heat layer as a React component
function HeatLayer({ points }: { points: [number, number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const heat = (L as any).heatLayer(points, {
      radius: 35,
      blur: 25,
      maxZoom: 17,
      minOpacity: 0.3,
      max: 1.0,
      gradient: { 0.1: '#ffffb2', 0.25: '#fecc5c', 0.4: '#fd8d3c', 0.6: '#f03b20', 0.8: '#bd0026', 1.0: '#800026' },
    }).addTo(map);
    return () => { map.removeLayer(heat); };
  }, [points, map]);
  return null;
}

// Fit bounds only once on initial data load
function FitBoundsOnce({ bounds }: { bounds: L.LatLngBoundsExpression | null }) {
  const map = useMap();
  const hasFit = useRef(false);
  useEffect(() => {
    if (bounds && !hasFit.current) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
      hasFit.current = true;
    }
  }, [bounds, map]);
  return null;
}

export default function DebrisMap() {
  const navigate = useNavigate();
  const { activeIncident } = useAuthStore();
  const incidentId = activeIncident?.id?.toString() ?? '';

  const { zones, disposalSites, loadingZones, loadingSites, loadZones, loadDisposalSites } = useDebrisStore();
  const { activeZones: geoopsZones } = useIncidentZones();
  const { loadTickets, loadingTickets, loadLoadTickets } = useDebrisOpsStore();

  const [showZones, setShowZones] = useState(true);
  const [showSites, setShowSites] = useState(true);
  const [showPickups, setShowPickups] = useState(true);
  const [showHeat, setShowHeat] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [tileLayer, setTileLayer] = useState('street');

  useEffect(() => { loadZones(); loadDisposalSites(); loadLoadTickets(); }, [loadZones, loadDisposalSites, loadLoadTickets]);

  const isLoading = loadingZones || loadingSites || loadingTickets;

  const iZones = incidentId ? zones : [];
  const iSites = incidentId ? disposalSites : [];
  const allTickets = incidentId ? loadTickets : [];
  const iTickets = useMemo(() => {
    if (typeFilter && typeFilter !== 'all') return allTickets.filter(tk => tk.debrisType === typeFilter);
    return allTickets;
  }, [allTickets, typeFilter]);

  // Compute bounds from ALL points (not affected by type filter so zoom is stable)
  const bounds = useMemo(() => {
    const points: [number, number][] = [];
    iZones.forEach(z => {
      const lat = parseFloat(z.centerLat); const lng = parseFloat(z.centerLng);
      if (lat && lng) points.push([lat, lng]);
    });
    iSites.forEach(s => {
      const lat = parseFloat(s.latitude); const lng = parseFloat(s.longitude);
      if (lat && lng) points.push([lat, lng]);
    });
    allTickets.forEach(t => {
      const lat = parseFloat(t.pickupLat); const lng = parseFloat(t.pickupLng);
      if (lat && lng) points.push([lat, lng]);
    });
    if (points.length === 0) return null;
    return L.latLngBounds(points);
  }, [iZones, iSites, allTickets]);

  // Parse zone boundaries
  const zoneBoundaries = useMemo(() =>
    iZones.map(z => {
      if (!z.boundaryGeojson) return null;
      try {
        const geo = JSON.parse(z.boundaryGeojson);
        if (geo.type === 'Polygon' && geo.coordinates?.[0]) {
          return {
            zone: z,
            positions: geo.coordinates[0].map((c: number[]) => [c[1], c[0]] as [number, number]),
          };
        }
      } catch { /* skip */ }
      return null;
    }).filter(Boolean) as Array<{ zone: typeof iZones[0]; positions: [number, number][] }>,
  [iZones]);

  // Heatmap data points: [lat, lng, intensity]
  const heatPoints = useMemo(() => {
    const raw: [number, number, number][] = [];
    for (const t of iTickets) {
      // Use individual stops for finer-grained heat
      try {
        const stops = JSON.parse(t.pickupStopsJson || '[]');
        if (Array.isArray(stops) && stops.length > 0) {
          for (const s of stops) {
            const lat = parseFloat(s.lat); const lng = parseFloat(s.lng);
            if (lat && lng) raw.push([lat, lng, parseFloat(s.unitCount || s.weight || '1') || 1]);
          }
          continue;
        }
      } catch { /* fall through */ }
      // Fallback to ticket centroid
      const lat = parseFloat(t.pickupLat); const lng = parseFloat(t.pickupLng);
      if (lat && lng) raw.push([lat, lng, parseFloat(t.calculatedCubicYards || '1') || 1]);
    }
    if (raw.length === 0) return [];
    // Normalize intensity to 0–1 so the gradient renders visibly
    const maxVal = Math.max(...raw.map(p => p[2]));
    return raw.map(([lat, lng, v]) => [lat, lng, maxVal > 0 ? Math.max(v / maxVal, 0.15) : 0.5] as [number, number, number]);
  }, [iTickets]);

  // Atlanta default (matches seed data)
  const defaultCenter: [number, number] = [33.749, -84.388];
  const DEBRIS_TYPE_SELECT = [{ value: 'all', label: 'All Types' }, ...Object.entries(DEBRIS_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))];

  const TILE_LAYERS: Record<string, { url: string; attribution: string }> = {
    street: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap' },
    satellite: { url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '&copy; Esri' },
    topo: { url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenTopoMap' },
    dark: { url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '&copy; CartoDB' },
  };
  const activeTile = TILE_LAYERS[tileLayer] ?? TILE_LAYERS.street;

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={3} c="black">Debris Map</Title>
        <Group gap="md">
          <Switch label="Zones" checked={showZones} onChange={e => setShowZones(e.currentTarget.checked)} size="xs" />
          <Switch label="Sites" checked={showSites} onChange={e => setShowSites(e.currentTarget.checked)} size="xs" />
          <Switch label="Pickups" checked={showPickups} onChange={e => setShowPickups(e.currentTarget.checked)} size="xs" />
          <Switch label="Heat" checked={showHeat} onChange={e => setShowHeat(e.currentTarget.checked)} size="xs" />
          <Select data={DEBRIS_TYPE_SELECT} value={typeFilter ?? 'all'} onChange={v => setTypeFilter(v)} size="xs" w={180} />
        </Group>
      </Group>

      {/* Layer selector + Legend */}
      <Group justify="space-between">
        <SegmentedControl size="xs" value={tileLayer} onChange={setTileLayer}
          data={[
            { value: 'street', label: 'Street' },
            { value: 'satellite', label: 'Satellite' },
            { value: 'topo', label: 'Terrain' },
            { value: 'dark', label: 'Dark' },
          ]} />
        <Group gap="lg">
          <Group gap={4}><div style={{ width: 12, height: 12, borderRadius: '50%', background: '#16a34a', border: '2px solid #fff' }} /><Text size="xs">Zones</Text></Group>
          <Group gap={4}><div style={{ width: 12, height: 12, borderRadius: '50%', background: '#dc2626', border: '2px solid #fff' }} /><Text size="xs">Disposal Sites</Text></Group>
          <Group gap={4}><div style={{ width: 12, height: 12, borderRadius: '50%', background: '#d97706', border: '2px solid #fff' }} /><Text size="xs">Pickups</Text></Group>
          {showHeat && <Group gap={4}><div style={{ width: 12, height: 12, borderRadius: '50%', background: '#d97706', opacity: 0.4 }} /><Text size="xs">Heat (CY density)</Text></Group>}
          <Text size="xs" c="dimmed">{iTickets.length} tickets mapped</Text>
          {isLoading && <Loader size="xs" />}
        </Group>
      </Group>

      <Paper withBorder style={{ height: 'calc(100vh - 260px)', minHeight: 400, borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
        {isLoading && iZones.length === 0 && iSites.length === 0 && allTickets.length === 0 && (
          <Center style={{ position: 'absolute', inset: 0, zIndex: 1000, background: 'rgba(255,255,255,0.8)' }}>
            <Stack align="center" gap="xs">
              <Loader size="lg" />
              <Text size="sm" c="dimmed">Loading map data...</Text>
            </Stack>
          </Center>
        )}
        <MapContainer center={defaultCenter} zoom={10} style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}>
          <TileLayer
            key={tileLayer}
            attribution={activeTile.attribution}
            url={activeTile.url}
          />
          <FitBoundsOnce bounds={bounds} />

          {/* GeoOps incident zones */}
          <ZoneMapOverlay zones={geoopsZones} interactive />

          {/* Zone boundaries */}
          {showZones && zoneBoundaries.map(({ zone, positions }) => (
            <Polygon key={`zb-${zone.id}`} positions={positions}
              pathOptions={{ color: '#16a34a', weight: 2, fillOpacity: 0.15 }}>
              <Popup>
                <Stack gap={4} style={{ minWidth: 180 }}>
                  <Text fw={600} size="sm">{zone.zoneName}</Text>
                  <Badge size="xs" color={zone.status === 'active' ? 'green' : 'gray'}>{zone.status}</Badge>
                  <Text size="xs">Est: {parseFloat(zone.estimatedCubicYards || '0').toLocaleString()} CY</Text>
                  <Button size="compact-xs" variant="light" onClick={() => navigate('/debris/zones')}>View Zones</Button>
                </Stack>
              </Popup>
            </Polygon>
          ))}

          {/* Zone center markers */}
          {showZones && iZones.map(z => {
            const lat = parseFloat(z.centerLat); const lng = parseFloat(z.centerLng);
            if (!lat || !lng) return null;
            return (
              <Marker key={`z-${z.id}`} position={[lat, lng]} icon={ZONE_ICON}>
                <Popup>
                  <Stack gap={4} style={{ minWidth: 200 }}>
                    <Text fw={600} size="sm">{z.zoneName} ({z.zoneCode})</Text>
                    <Badge size="xs" color={z.status === 'active' ? 'green' : 'gray'}>{z.status}</Badge>
                    <Group gap="xs">
                      <Text size="xs">Est: {parseFloat(z.estimatedCubicYards || '0').toLocaleString()} CY</Text>
                      <Text size="xs" fw={600}>Actual: {parseFloat(z.actualCubicYards || '0').toLocaleString()} CY</Text>
                    </Group>
                    <Button size="compact-xs" variant="light" onClick={() => navigate('/debris/zones')}>View Zone Details</Button>
                  </Stack>
                </Popup>
              </Marker>
            );
          })}

          {/* Disposal sites */}
          {showSites && iSites.map(s => {
            const lat = parseFloat(s.latitude); const lng = parseFloat(s.longitude);
            if (!lat || !lng) return null;
            const cap = parseFloat(s.capacityCubicYards || '0');
            const vol = parseFloat(s.currentVolume || '0');
            const pct = cap > 0 ? ((vol / cap) * 100).toFixed(0) : '?';
            return (
              <Marker key={`s-${s.id}`} position={[lat, lng]} icon={SITE_ICON}>
                <Popup>
                  <Stack gap={4} style={{ minWidth: 220 }}>
                    <Text fw={600} size="sm">{s.siteName}</Text>
                    <Group gap={6}>
                      <Badge size="xs" tt="capitalize">{s.siteType}</Badge>
                      <Badge size="xs" color={s.status === 'open' ? 'green' : s.status === 'full' ? 'orange' : 'gray'}>{s.status}</Badge>
                    </Group>
                    <Text size="xs">Fill: {pct}% ({vol.toLocaleString()} / {cap.toLocaleString()} CY)</Text>
                    {s.monitorName && <Text size="xs" c="dimmed">Monitor: {s.monitorName}</Text>}
                    <Button size="compact-xs" variant="light" onClick={() => navigate('/debris/sites')}>View Site Details</Button>
                  </Stack>
                </Popup>
              </Marker>
            );
          })}

          {/* Individual pickup stops from load tickets */}
          {showPickups && iTickets.flatMap(t => {
            const stops: any[] = (() => { try { return JSON.parse(t.pickupStopsJson || '[]'); } catch { return []; } })();
            if (stops.length > 0) {
              // Render individual stop markers
              return stops.map((s: any, idx: number) => {
                const sLat = parseFloat(s.lat); const sLng = parseFloat(s.lng);
                if (!sLat || !sLng) return null;
                return (
                  <Marker key={`stop-${t.id}-${idx}`} position={[sLat, sLng]} icon={STOP_ICON}>
                    <Popup>
                      <Stack gap={4} style={{ minWidth: 200 }}>
                        <Text fw={600} size="sm">{t.ticketNumber} — Stop {idx + 1}</Text>
                        {s.debrisType && <Badge size="xs" variant="light">{DEBRIS_TYPE_LABELS[s.debrisType as DebrisType] ?? s.debrisType}</Badge>}
                        {s.address && <Text size="xs" c="dimmed">{s.address}</Text>}
                        {s.unitCount && <Text size="xs">{s.unitCount} {s.debrisType === 'vegetative' ? 'CY' : 'units'}</Text>}
                        <Badge size="xs" color={t.status === 'delivered' ? 'green' : t.status === 'voided' ? 'red' : 'orange'}>{t.status?.replace(/_/g, ' ')}</Badge>
                      </Stack>
                    </Popup>
                  </Marker>
                );
              });
            }
            // Fallback: if no stops, use ticket-level centroid
            const lat = parseFloat(t.pickupLat); const lng = parseFloat(t.pickupLng);
            if (!lat || !lng) return [];
            return [(
              <Marker key={`p-${t.id}`} position={[lat, lng]} icon={PICKUP_ICON}>
                <Popup>
                  <Stack gap={4} style={{ minWidth: 220 }}>
                    <Text fw={600} size="sm">{t.ticketNumber}</Text>
                    <Group gap={6}>
                      <Badge size="xs" variant="light">{DEBRIS_TYPE_LABELS[t.debrisType as DebrisType] ?? t.debrisType}</Badge>
                      <Badge size="xs" color={t.status === 'delivered' ? 'green' : t.status === 'voided' ? 'red' : 'orange'}>{t.status?.replace(/_/g, ' ')}</Badge>
                    </Group>
                    <Text size="xs">{parseFloat(t.calculatedCubicYards || '0').toLocaleString()} CY</Text>
                    {t.pickupAddress && <Text size="xs" c="dimmed">{t.pickupAddress}</Text>}
                    {t.monitorName && <Text size="xs" c="dimmed">Monitor: {t.monitorName}</Text>}
                    <Button size="compact-xs" variant="light" onClick={() => navigate('/debris/tickets')}>View Ticket Details</Button>
                  </Stack>
                </Popup>
              </Marker>
            )];
          })}

          {/* Heat overlay — real heatmap via leaflet.heat */}
          {showHeat && heatPoints.length > 0 && <HeatLayer points={heatPoints} />}
        </MapContainer>
      </Paper>
    </Stack>
  );
}
